import React from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, EmptyState, SkeletonRows } from "@/components/dash/ui";
import { formatDistanceToNow } from "date-fns";
import { Download, FileBarChart, LayoutTemplate, TrendingUp, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

export function ActivityPage() {
  const [items, setItems] = React.useState(null);
  React.useEffect(() => { api.get("/activity-logs", { params: { page_size: 60 } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  return (
    <div>
      <PageHeader title="Activity Center" description="Chronological history of workspace events." breadcrumb="Overview / Activity" />
      <Card>
        {items === null ? <SkeletonRows /> : items.length === 0 ? <EmptyState icon="Activity" title="No activity yet" /> : (
          <div className="relative pl-6 space-y-5">
            <div className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200" />
            {items.map((a) => (
              <div key={a.id} className="relative">
                <div className="absolute -left-[22px] top-1 h-3.5 w-3.5 rounded-full bg-vs-royal ring-4 ring-vs-light" />
                <div className="text-sm text-slate-700"><span className="font-medium">{a.actor_name}</span> {a.action} <span className="text-slate-400">· {a.entity_type?.replace(/_/g, " ")}</span></div>
                <div className="text-xs text-slate-400">{a.timestamp ? formatDistanceToNow(new Date(a.timestamp), { addSuffix: true }) : ""}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export function NotificationsPage() {
  const [items, setItems] = React.useState(null);
  const load = () => api.get("/notifications", { params: { page_size: 60 } }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  React.useEffect(() => { load(); }, []);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <div>
      <PageHeader title="Notifications" description="All your workspace alerts and updates." breadcrumb="Communications / Notifications">
        <Button variant="outline" onClick={markAll} data-testid="notif-mark-all">Mark all read</Button>
      </PageHeader>
      <Card>
        {items === null ? <SkeletonRows /> : items.length === 0 ? <EmptyState icon="Bell" title="You're all caught up" /> : (
          <div className="divide-y divide-slate-100">
            {items.map((n) => (
              <div key={n.id} className={`flex gap-3 py-3 ${!n.read ? "bg-blue-50/30 -mx-5 px-5" : ""}`}>
                <div className="h-9 w-9 rounded-lg bg-vs-light flex items-center justify-center shrink-0"><Bell className="h-4 w-4 text-vs-royal" /></div>
                <div className="min-w-0"><div className="text-sm font-medium text-slate-800">{n.title}</div>
                  <div className="text-sm text-slate-500">{n.message}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}</div></div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export function TemplatesPage() {
  const [items, setItems] = React.useState([]);
  React.useEffect(() => { api.get("/search", { params: { q: "welcome" } }).catch(() => {}); }, []);
  const TEMPLATES = ["Welcome", "Registration", "Verification", "Approval", "Rejection", "RFQ Invitation", "Quotation Submission", "Candidate Submission", "Shortlisting", "Interview", "Selection", "Purchase Order", "Contract", "Invoice", "Payment", "Document Expiry", "Password Reset", "Support"];
  return (
    <div>
      <PageHeader title="Email Templates" description="Branded transactional email templates (VS PartnerHub)." breadcrumb="Communications / Templates" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {TEMPLATES.map((t) => (
          <Card key={t} className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-vs-light flex items-center justify-center"><LayoutTemplate className="h-5 w-5 text-vs-royal" /></div>
            <div><div className="font-medium text-slate-800 text-sm">{t}</div><div className="text-xs text-emerald-600">Active</div></div></Card>
        ))}
      </div>
    </div>
  );
}

export function ReportsPage() {
  const REPORTS = [
    { name: "Requirements Report", endpoint: "/requirements" }, { name: "RFQ & Quotations", endpoint: "/quotations" },
    { name: "Candidate Submissions", endpoint: "/submissions" }, { name: "Invoices & Payments", endpoint: "/invoices" },
    { name: "Contracts", endpoint: "/contracts" }, { name: "Partner Directory", endpoint: "/organizations" },
  ];
  const run = async (r) => {
    try {
      const { data } = await api.get(r.endpoint, { params: { page_size: 1000 } });
      const rows = data.items || [];
      if (!rows.length) return toast.info("No data to export");
      const keys = Object.keys(rows[0]).filter((k) => k !== "_id");
      const csv = [keys.join(","), ...rows.map((row) => keys.map((k) => `"${String(row[k] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `${r.name}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success("Report exported");
    } catch { toast.error("Unable to generate report"); }
  };
  return (
    <div>
      <PageHeader title="Reports & Export" description="Generate and export business reports." breadcrumb="Analytics / Reports" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.map((r) => (
          <Card key={r.name}>
            <div className="flex items-center gap-3 mb-4"><div className="h-10 w-10 rounded-lg bg-vs-light flex items-center justify-center"><FileBarChart className="h-5 w-5 text-vs-royal" /></div>
              <div className="font-medium text-slate-800">{r.name}</div></div>
            <Button variant="outline" className="w-full" onClick={() => run(r)} data-testid={`report-${r.name.replace(/\s/g,'-').toLowerCase()}`}><Download className="h-4 w-4 mr-1.5" />Export CSV</Button>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function PartnerPerformancePage() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => { api.get("/analytics/partner-performance").then((r) => setData(r.data.data)).catch(() => setData([])); }, []);
  return (
    <div>
      <PageHeader title="Partner Performance" description="Operational metrics across vendors and staffing partners." breadcrumb="Analytics / Partner Performance" />
      <Card>
        {data === null ? <SkeletonRows /> : data.length === 0 ? <EmptyState icon="TrendingUp" title="No performance data yet" /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <th className="py-2.5 font-semibold">Partner</th><th className="py-2.5 font-semibold">Type</th><th className="py-2.5 font-semibold">Activity</th><th className="py-2.5 font-semibold">Success</th><th className="py-2.5 font-semibold">Rating</th></tr></thead>
            <tbody>{data.map((p) => (<tr key={p.name} className="border-b border-slate-50">
              <td className="py-3 font-medium text-slate-800">{p.name}</td><td className="py-3 capitalize text-slate-600">{p.type}</td>
              <td className="py-3 text-slate-600">{p.activity}</td><td className="py-3 text-slate-600">{p.success}</td>
              <td className="py-3 text-amber-500 font-semibold">★ {p.rating}</td></tr>))}</tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
}

export function SettingsPage() {
  return (
    <div>
      <PageHeader title="System Settings" description="Platform configuration and branding." breadcrumb="Administration / Settings" />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Company Information">
          <div className="space-y-2 text-sm">
            <Row k="Platform" v="VS PartnerHub" /><Row k="Company" v="Vijay Software Solutions Pvt Ltd" />
            <Row k="Brand" v="VS-Solutions" /><Row k="Website" v="www.vs-solutions.in" />
            <Row k="Tagline" v="Connect. Source. Collaborate. Grow." />
          </div>
        </Card>
        <CommSettings />
        <Card title="Platform Modules">
          <div className="grid grid-cols-2 gap-2">
            {["Requirements", "RFQ Engine", "Procurement", "Staffing", "Documents", "Notifications", "Email (Resend)", "VS AI", "Audit Logs", "Analytics"].map((m) => (
              <div key={m} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2"><span className="text-sm text-slate-700">{m}</span><StatusBadge status="active" /></div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function CommSettings() {
  const [cfg, setCfg] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const load = () => api.get("/settings/communication").then((r) => setCfg(r.data)).catch(() => setCfg(false));
  React.useEffect(() => { load(); }, []);
  if (cfg === null) return <Card title="Communication / Email Settings"><SkeletonRows /></Card>;
  if (cfg === false) return null;
  const set = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/communication", cfg);
      setCfg(data); toast.success("Communication settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const F = ({ k, label, testid }) => (
    <div><label className="text-xs font-semibold text-slate-600">{label}</label>
      <input value={cfg[k] || ""} onChange={set(k)} data-testid={testid}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-vs-royal outline-none" /></div>
  );
  return (
    <Card title="Communication / Email Settings">
      <div className="space-y-3">
        <F k="from_name" label="Platform From Name" testid="comm-from-name" />
        <div className="grid sm:grid-cols-2 gap-3">
          <F k="admin_cc" label="Admin CC" testid="comm-admin-cc" />
          <F k="reply_to" label="Reply-To" testid="comm-reply-to" />
          <F k="info_email" label="Information Email" testid="comm-info-email" />
          <F k="support_email" label="Support Email" testid="comm-support-email" />
        </div>
        <p className="text-[11px] text-slate-400">Every transactional email CCs Admin CC + Information Email (duplicates removed automatically). Support tickets are routed to the Support Email.</p>
        <Button onClick={save} disabled={saving} className="bg-vs-royal text-white" data-testid="comm-save-btn">{saving ? "Saving…" : "Save Settings"}</Button>
      </div>
    </Card>
  );
}
const Row = ({ k, v }) => (<div className="flex justify-between py-1.5 border-b border-slate-50"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v}</span></div>);
